import type { ReactNode } from "react";

import { Loader2 } from "@/components/icons";
import { genericFallback } from "@/lib/fonts";
import type { FontScheme } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Accent palette keys shown as a small dot row under the heading sample. */
const ACCENT_KEYS = ["primary", "secondary", "success", "error"];

/**
 * A "what your page would look like" preview: the theme's own real
 * background/text colors and heading/body fonts (as rendered in the
 * uploader), not a swatch strip or a font name printed in the UI's own
 * font. Shared by the template grid and the AI-generated look preview so
 * both read as the exact same kind of card.
 */
export function ThemeCardPreview({
  palette,
  fontScheme,
  heading,
  label,
  loading,
  badge,
  className,
}: {
  palette: Record<string, string>;
  fontScheme: FontScheme;
  /** Live heading sample — the couple's own display name. */
  heading: string;
  /** Template name, or "Your AI look" for the generated preview. */
  label: string;
  loading?: boolean;
  /** Overlay content, e.g. a selected/pending corner badge. */
  badge?: ReactNode;
  className?: string;
}) {
  const bg = palette.background ?? "#111111";
  const onBg = palette["on-background"] ?? "#ffffff";
  const headingStack = `"${fontScheme.heading.family}", ${genericFallback(fontScheme.heading.category)}`;
  const bodyStack = `"${fontScheme.body.family}", ${genericFallback(fontScheme.body.category)}`;

  return (
    <div
      className={cn("relative space-y-3 rounded-lg p-4", className)}
      style={{ backgroundColor: bg, color: onBg }}
    >
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      <div className="space-y-1 text-center">
        <p className="truncate text-xl" style={{ fontFamily: headingStack }}>
          {heading}
        </p>
        <p className="truncate text-xs opacity-80" style={{ fontFamily: bodyStack }}>
          Every moment, beautifully kept.
        </p>
      </div>
      <div
        className="flex items-center justify-between gap-2 pt-2"
        style={{ borderTop: `1px solid ${onBg}33` }}
      >
        <span className="truncate text-sm font-medium" style={{ fontFamily: bodyStack }}>
          {label}
        </span>
        <div className="flex items-center gap-1">
          {ACCENT_KEYS.map((key) => (
            <span
              key={key}
              className="h-3.5 w-3.5 rounded-full border"
              style={{ backgroundColor: palette[key] ?? "#000000", borderColor: `${onBg}33` }}
            />
          ))}
        </div>
      </div>
      {badge}
    </div>
  );
}
